import React, {useEffect, useState} from 'react';
import {buyItemRequest, createBuyOfferRequest} from 'util/api';
import InputMoney from 'util/InputMoney';
import OfferList from 'components/items/detail/OfferList';
import AddMoneyPopupWrapper from 'components/donate/AddMoneyPopupWrapper';
import {UserContext} from 'components/user/UserContext';
import {usePopup} from 'popup/PopupContext';
import {centsToString} from 'util/cashFormatter';
import itemRarityColors from 'util/itemRarityColors';
import itemTypeToEmoji from 'util/itemTypeToEmoji';

const BuyTab = ({item}) => {
    const [buyQuantity, setBuyQuantity] = useState(1);
    const [buyPrice, setBuyPrice] = useState(0);
    const [showCreateBuyOffer, setShowCreateBuyOffer] = useState(false);
    const [constantLabel, setConstantLabel] = useState('');
    const [isPriceEqualOrGreaterThanSellOffer, setIsPriceEqualOrGreaterThanSellOffer] = useState(false);
    const {user} = React.useContext(UserContext);
    const {openPopup} = usePopup();

    useEffect(() => {
        setIsPriceEqualOrGreaterThanSellOffer(buyPrice >= item.getUserSo() && item.isUserSellOffer());
    }, [buyPrice, item]);

    const handleBuyNow = async () => {
        const userBalance = user.balance;
        const totalCost = item.getMarketSo();

        if (userBalance < totalCost) {
            openPopup(<AddMoneyPopupWrapper initialAmount={(totalCost - userBalance) + ''}/>);
            return;
        }
        setConstantLabel('Buy ' + centsToString(item.getMarketSo()));
        await buyItemRequest(item.itemType, item.getMarketSo(), user.balance);
        await new Promise(resolve => setTimeout(resolve, 300));
        setConstantLabel('');
    };

    const handleCreateBuyOffer = async () => {
        if (buyPrice < 1) {
            return;
        }
        const userBalance = user.balance;
        const totalCost = buyPrice * buyQuantity;

        if (userBalance < totalCost) {
            openPopup(<AddMoneyPopupWrapper initialAmount={(totalCost - userBalance)}/>);
            return;
        }
        try {
            await createBuyOfferRequest(item.itemType, buyQuantity, buyPrice, userBalance);
            setShowCreateBuyOffer(false);
        } catch (error) {
            console.error('Error creating buy offer:', error);
        }
    };

    const handleCancelBuy = async () => {
        try {
            await createBuyOfferRequest(item.itemType, 0, 0, user.balance);
            setShowCreateBuyOffer(false);
        } catch (error) {
            console.error('Error cancelling buy offer:', error);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            if (showCreateBuyOffer) {
                handleCreateBuyOffer();
            } else {
                handleBuyNow();
            }
        }
    };

    const handleShowSellOffers = () => {
        openPopup(<OfferList isSell={true} itemType={item.itemType}/>);
    };

    const rarityColors = itemRarityColors(item); // Fetch rarity colors for the item

    const isMyBestSo = item.getMarketSo() === item.getUserSo();
    const isBuyDisabled = isMyBestSo || !item.getMarketSo() || constantLabel;
    return (
        <div onKeyDown={handleKeyDown} tabIndex="0">
            <div className="center-button-container">

                <div
                    onClick={!isBuyDisabled ? handleBuyNow : undefined} // Disable click event if isMyBestSo is true
                    className={
                        'beautiful-buy-button ' +
                        (isBuyDisabled ? 'disabled' : 'enabled')
                    }
                    style={{
                        backgroundColor: isBuyDisabled ? rarityColors.disabledActionButtonColor : rarityColors.actionButtonColor
                    }}
                    data-tooltip-id="main-tooltip"
                    data-tooltip-content={isBuyDisabled ? (item.getUserSo() ? 'You are selling for ' + centsToString(item.getUserSo()) : 'Nobody is selling ' + itemTypeToEmoji[item.itemType].translation) : ''}
                >
                    {
                        constantLabel ? constantLabel : (
                            `Buy${item.getMarketSo() ? ' ' + centsToString(item.getMarketSo()) : ''}`
                        )
                    }

                </div>
            </div>
            {showCreateBuyOffer && !item.isUserBuying() && (
                <div>
                    <h3>
                        Create Buy Offer
                        <span
                            style={{color: 'red', cursor: 'pointer', marginLeft: '10px'}}
                            onClick={() => setShowCreateBuyOffer(false)}
                        >
                            &#x2716;
                        </span>
                    </h3>
                    <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap'}}>
                        <div style={{marginRight: '1em'}}>
                            <label>
                                Price: $
                                <InputMoney
                                    initialValue={buyPrice}
                                    onChange={setBuyPrice}
                                    nameClass={'item-detail-input-field'}
                                />
                            </label>
                        </div>
                        <div style={{marginRight: '1em'}}>
                            <label>
                                Buy Quantity:{' '}
                                <input
                                    type="number"
                                    value={buyQuantity}
                                    min={1}
                                    onChange={(e) => setBuyQuantity(Number(e.target.value))}
                                    style={{width: '2.5em'}}
                                    className="item-detail-input-field"
                                />
                            </label>
                        </div>
                        <div>
                            <button
                                onClick={handleCreateBuyOffer}
                                style={{display: 'block', margin: '0 auto'}}
                                disabled={isNaN(buyPrice) || isPriceEqualOrGreaterThanSellOffer || buyPrice < 1}
                            >
                                Create offer
                                {isNaN(buyPrice) || isPriceEqualOrGreaterThanSellOffer || buyPrice < 1 ? '' : (' for ' + (centsToString(buyPrice * buyQuantity)))}
                            </button>
                            {isPriceEqualOrGreaterThanSellOffer && (
                                <p style={{color: 'red', fontSize: 'x-small'}}>
                                    You are selling for {centsToString(item.getMarketSo())} each
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div style={{display: 'flex', justifyContent: 'space-between'}}>
                {!showCreateBuyOffer && !item.isUserBuying() && (
                    <button onClick={() => setShowCreateBuyOffer(!showCreateBuyOffer)}>
                        Offer Price
                    </button>
                )}
                {item.isUserBuying() && (
                    <p>
                        You are
                        buying {item.getUserBuyingQuantity()}{item.getUserBuyingQuantity() === 1 ? '' : ', each'} for {centsToString(item.getUserBo())}
                        <span
                            style={{color: 'red', cursor: 'pointer', marginLeft: '10px'}}
                            onClick={handleCancelBuy}
                        >
                        &#x2716;
                    </span>
                    </p>
                )}
                <div/>
                {item.isMarketSo() && (
                    <div className="button-container">
                        <button onClick={handleShowSellOffers}>
                            Show People Selling
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BuyTab;
